<template>
  <Transition :name="isMobile ? 'fade-absolute' : 'reveal-from-top'">
    <div
      v-show="visible"
      class="page-menu-navigation left-0 w-full tablet:absolute"
      @click.capture="onClick"
    >
      <div ref="el" class="tablet:bg-background tablet:px-section-x">
        <div class="container">
          <div
            class="reveal-from-top-inner grid tablet:grid-cols-3 tablet:gap-6 tablet:py-8 desktop:grid-cols-4"
          >
            <div
              id="quick-links"
              class="tablet:border-r tablet:border-r-border"
            >
              <div class="tablet:pr-2">
                <h3 class="mt-4 text-size-6 font-bold text-muted tablet:mt-0">
                  {{ $texts('menu.quickLinks', 'Quick Links') }}
                </h3>
                <BlokkliField
                  :list="quickLinks"
                  name="field_paragraphs_cta"
                  field-list-type="menu"
                  class="grid gap-1 tablet:gap-2 tablet:border-b tablet:border-b-border tablet:pb-4"
                />
              </div>
              <BlokkliField
                :list="teasers"
                name="field_paragraphs"
                field-list-type="menu"
                class="grid gap-4 tablet:pr-4 tablet:pt-4"
              />
            </div>

            <Links
              :links="links"
              class="order-first tablet:order-last tablet:col-span-2 desktop:col-span-3"
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { FieldListItem } from '#blokkli/types'
import type { MenuLinkTreeFirstFragment } from '#graphql-operations'
import { isDomEventTarget } from '~/helpers/dom'
import Links from './../Links/index.vue'
import { onKeyStroke } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

const props = defineProps<{
  visible?: boolean
  quickLinks?: FieldListItem[]
  teasers?: FieldListItem[]
  links: MenuLinkTreeFirstFragment[]
}>()

const emit = defineEmits(['close'])

const { isMobile } = useViewport()
const { $texts } = useEasyTexts()
const { trackEvent } = useAnalytics()

const el = ref<HTMLElement>()
const { activate, deactivate } = useFocusTrap(el, {
  immediate: false,
  allowOutsideClick: true,
  initialFocus: false,
})

watch(
  () => props.visible,
  async function (shouldTrap) {
    // Wait until the element is visible.
    await nextTick()

    if (shouldTrap) {
      activate()
    } else {
      deactivate()
    }
  },
)

function onClick(e: MouseEvent) {
  if (!isDomEventTarget(e)) {
    return
  }

  const link = e.target.closest('a')
  if (!link) {
    return
  }

  const name = link.innerText.trim()
  const href = link.href
  const trackIdElement = e.target.closest('[data-track-id]')
  const id =
    trackIdElement instanceof HTMLElement
      ? trackIdElement.dataset.trackId
      : 'unknown'

  trackEvent({
    event: 'click_menu_element',
    element_name: name,
    element_id: 'menu-element-' + id,
    element_url: href,
  })
}

onKeyStroke(['Escape'], () => emit('close'))
</script>

<style lang="postcss">
.page-menu-navigation {
  @screen tablet {
    top: calc(var(--page-menu-top) + var(--page-menu-bottom) - 1px);
  }
}
</style>
